import React, { useEffect } from 'react'
import { Row, Col, Card, Statistic, Button, Alert, Progress, Tag, Typography, Space } from 'antd'
import {
  FileTextOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { fetchCurrentLead, getNextLead } from '../store/slices/leadSlice'
import { fetchPersonalStats } from '../store/slices/statsSlice'

const { Title, Text } = Typography

function Dashboard() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const { currentLead, loading: leadLoading } = useAppSelector((state) => state.leads)
  const { personalStats, loading: statsLoading } = useAppSelector((state) => state.stats)

  useEffect(() => {
    dispatch(fetchCurrentLead())
    dispatch(fetchPersonalStats('today'))
  }, [dispatch])

  const handleGetNextLead = async () => {
    await dispatch(getNextLead())
    navigate('/lead')
  }

  const handleContinueLead = () => {
    navigate('/lead')
  }

  const statsCards = [
    {
      title: 'Worked On / Released',
      value: `${personalStats?.total_worked_on || 0} / ${personalStats?.leads_released || 0}`,
      prefix: <FileTextOutlined />,
      valueStyle: { color: '#1976d2' },
    },
    {
      title: 'Completed',
      value: `${personalStats?.leads_completed || 0} / ${personalStats?.total_worked_on || 0}`,
      prefix: <CheckCircleOutlined />,
      valueStyle: { color: '#52c41a' },
    },
    {
      title: 'Success Rate',
      value: ((personalStats?.success_rate || 0) * 100).toFixed(1),
      suffix: '%',
      prefix: <CheckCircleOutlined />,
      valueStyle: { color: '#52c41a' },
    },
    {
      title: 'Avg Time',
      value: personalStats?.average_time_minutes || 0,
      suffix: 'min',
      prefix: <ClockCircleOutlined />,
      valueStyle: { color: '#faad14' },
    },
  ]

  return (
    <div>
      <Title level={2}>Welcome back, {user?.name}!</Title>
      
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {statsCards.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card>
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={stat.prefix}
                suffix={stat.suffix}
                valueStyle={stat.valueStyle}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={16}>
          <Card
            title="Current Lead Status"
            loading={leadLoading}
          >
            {currentLead ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  message="You have an active lead assigned"
                  type="info"
                  showIcon
                />
                
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Text type="secondary">Company</Text>
                    <Title level={4} style={{ marginTop: '4px' }}>
                      {currentLead.company_name}
                    </Title>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary">Lead Score</Text>
                    <Title level={4} style={{ marginTop: '4px' }}>
                      {currentLead.lead_score}
                    </Title>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary">Source</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Tag color={currentLead.is_crm_sourced ? 'blue' : 'default'}>
                        {currentLead.source_type}
                      </Tag>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary">Time Remaining</Text>
                    <Title level={4} style={{ marginTop: '4px', color: '#faad14' }}>
                      {Math.floor((currentLead.time_remaining_seconds || 0) / 60)} minutes
                    </Title>
                  </Col>
                </Row>
                
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleContinueLead}
                  block
                  size="large"
                >
                  Continue Processing Lead
                </Button>
              </Space>
            ) : (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  message="No active lead. Ready to get the next one?"
                  type="success"
                  showIcon
                />
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={handleGetNextLead}
                  block
                  size="large"
                >
                  Get Next Lead
                </Button>
              </Space>
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title="Today's Breakdown">
            {personalStats && (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">By Source</Text>
                  <div style={{ marginTop: '8px' }}>
                    <Tag>Palladium: {personalStats.by_source.palladium}</Tag>
                    <Tag>CRM: {personalStats.by_source.crm}</Tag>
                    <Tag>API: {personalStats.by_source.api}</Tag>
                  </div>
                </div>
                
                <div>
                  <Text type="secondary">Performance</Text>
                  <Progress
                    percent={(personalStats.success_rate || 0) * 100}
                    strokeColor="#52c41a"
                    style={{ marginTop: '8px' }}
                  />
                </div>
                
                <Button
                  onClick={() => navigate('/stats')}
                  block
                  icon={<RightOutlined />}
                >
                  View Detailed Stats
                </Button>
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard